// frontend/src/pages/AdminBetaFeedback.js
// Admin-only Beta Feedback panel
// Reads target="beta" channel and allows admin replies
//
// UX upgrades:
// - Search (author/text)
// - Filter: Unreplied / Replied / All
// - Collapsible threads (keeps page light)
// - Load more (cursor-based) so it doesn't render 2000 items at once
//
// Backend contracts used:
// - GET   /api/feedback/live?target=beta&limit=...&before=...
// - POST  /api/feedback  (admin logged in)
// - PATCH /api/feedback/:id/archive (admin only)
//
// Note: This does NOT implement notifications. It only stores replies and displays them.

import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import { api } from "../api";
import { useUser } from "../UserContext";
import AdminLayout from "./AdminLayout";

function safeStr(v) {
  return typeof v === "string" ? v : "";
}

function fmtTime(v) {
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  } catch {
    return "";
  }
}

function normalize(s) {
  return safeStr(s).trim().toLowerCase();
}

export default function AdminBetaFeedback({ onGo }) {
  const { isAdmin } = useUser();

  const [items, setItems] = useState([]); // raw flat list
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [error, setError] = useState("");

  const [replyMap, setReplyMap] = useState({});
  const [busyReplyId, setBusyReplyId] = useState("");
  const [busyArchiveId, setBusyArchiveId] = useState("");

  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("unreplied"); // unreplied | replied | all
  const [expanded, setExpanded] = useState({}); // parentId -> bool

  const nextBeforeRef = useRef(""); // cursor from API
  const reachedEndRef = useRef(false);

  const PAGE_SIZE = 60;

  async function loadFirstPage() {
    setError("");
    setLoading(true);
    setItems([]);
    nextBeforeRef.current = "";
    reachedEndRef.current = false;

    try {
      const data = await api(`/api/feedback/live?target=beta&limit=${PAGE_SIZE}`, { method: "GET" });
      if (data?.ok) {
        const arr = Array.isArray(data.items) ? data.items : [];
        setItems(arr);
        nextBeforeRef.current = safeStr(data.nextBefore);
        reachedEndRef.current = !safeStr(data.nextBefore);
      } else {
        setItems([]);
        nextBeforeRef.current = "";
        reachedEndRef.current = true;
      }
    } catch (e) {
      setError("Failed to load beta feedback.");
      setItems([]);
      nextBeforeRef.current = "";
      reachedEndRef.current = true;
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (loadingMore) return;
    if (reachedEndRef.current) return;

    const before = safeStr(nextBeforeRef.current);
    if (!before) {
      reachedEndRef.current = true;
      return;
    }

    setError("");
    setLoadingMore(true);
    try {
      const data = await api(
        `/api/feedback/live?target=beta&limit=${PAGE_SIZE}&before=${encodeURIComponent(before)}`,
        { method: "GET" }
      );

      if (data?.ok) {
        const arr = Array.isArray(data.items) ? data.items : [];
        setItems((prev) => {
          const seen = new Set(prev.map((x) => safeStr(x?.id)));
          const merged = [...prev];
          for (const it of arr) {
            const id = safeStr(it?.id);
            if (!id || seen.has(id)) continue;
            seen.add(id);
            merged.push(it);
          }
          return merged;
        });

        nextBeforeRef.current = safeStr(data.nextBefore);
        reachedEndRef.current = !safeStr(data.nextBefore) || arr.length === 0;
      } else {
        reachedEndRef.current = true;
      }
    } catch (e) {
      setError("Failed to load more feedback.");
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const { topLevel, repliesByParent, repliedSet } = useMemo(() => {
    const list = Array.isArray(items) ? items : [];

    const top = [];
    const map = {};
    const replied = new Set();

    for (const it of list) {
      const pid = safeStr(it?.parentId).trim();
      const id = safeStr(it?.id).trim();
      if (pid) {
        if (!map[pid]) map[pid] = [];
        map[pid].push(it);
        replied.add(pid);
      } else {
        top.push(it);
      }
    }

    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => new Date(a?.createdAt).getTime() - new Date(b?.createdAt).getTime());
    });

    top.sort((a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime());

    return { topLevel: top, repliesByParent: map, repliedSet: replied };
  }, [items]);

  const visibleTopLevel = useMemo(() => {
    const query = normalize(q);

    return topLevel.filter((it) => {
      const id = safeStr(it?.id).trim();
      const hasReplies = repliedSet.has(id);

      if (filter === "unreplied" && hasReplies) return false;
      if (filter === "replied" && !hasReplies) return false;

      if (!query) return true;

      const hay =
        normalize(it?.author) +
        " " +
        normalize(it?.text) +
        " " +
        normalize(it?.storyTitle) +
        " " +
        normalize(it?.source);

      return hay.includes(query);
    });
  }, [topLevel, repliedSet, q, filter]);

  function toggleExpand(id) {
    const key = safeStr(id).trim();
    if (!key) return;
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function setExpand(id, v) {
    const key = safeStr(id).trim();
    if (!key) return;
    setExpanded((prev) => ({ ...prev, [key]: v === true }));
  }

  function preview(text, max = 240) {
    const t = safeStr(text).trim();
    if (t.length <= max) return t;
    return t.slice(0, max).trim() + "…";
  }

  async function submitReply(parentId) {
    const pid = safeStr(parentId).trim();
    if (!pid) return;

    const text = safeStr(replyMap[pid]).trim();
    if (!text) return;

    setError("");
    setBusyReplyId(pid);

    try {
      await api("/api/feedback", {
        method: "POST",
        body: JSON.stringify({
          text: text.slice(0, 2000),
          target: "beta",
          parentId: pid,
          source: "admin_beta_feedback",
          storyId: "",
          storyTitle: "Beta Feedback",
          forum: "",
        }),
      });

      setReplyMap((prev) => ({ ...prev, [pid]: "" }));

      await loadFirstPage();
      setExpand(pid, true);
    } catch (e) {
      setError("Failed to post reply.");
    } finally {
      setBusyReplyId("");
    }
  }

  async function archiveThread(threadId) {
    const id = safeStr(threadId).trim();
    if (!id) return;
    if (busyArchiveId) return;

    const ok = window.confirm("Archive this beta feedback thread? It will disappear from the list.");
    if (!ok) return;

    setError("");
    setBusyArchiveId(id);

    try {
      await api(`/api/feedback/${id}/archive`, { method: "PATCH" });
      await loadFirstPage();
    } catch (e) {
      console.error("Archive failed:", e);
      setError("Archive failed.");
    } finally {
      setBusyArchiveId("");
    }
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 820, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>Admin Only</h2>
          <p style={{ color: "#475569" }}>This page is restricted to administrators.</p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <button className="ns-button ns-button-outline" type="button" onClick={() => (onGo ? onGo("home") : null)}>
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout active="beta" onGo={onGo}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
        <div>
          <h2 style={{ marginTop: 0, marginBottom: 6 }}>Beta Feedback</h2>
          <div style={{ color: "#64748b", fontWeight: 700, fontSize: 13 }}>
            Read what testers felt. Reply with care. This is where trust is forged.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button className="ns-button ns-button-outline" type="button" onClick={loadFirstPage} disabled={loading || loadingMore}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      <div style={{ height: 12 }} />

      {error ? <div className="banner">{error}</div> : null}

      <div
        className="card"
        style={{
          marginTop: 12,
          padding: 12,
          border: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: "1 1 320px" }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>Search</div>
            <input
              className="ns-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search author or text…"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                outline: "none",
                fontWeight: 700,
              }}
            />
          </div>

          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>Filter</div>
            <select
              className="ns-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                outline: "none",
                fontWeight: 800,
                background: "#fff",
              }}
            >
              <option value="unreplied">Unreplied</option>
              <option value="replied">Replied</option>
              <option value="all">All</option>
            </select>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span className="ns-pill admin-beta-pill">
              Showing: <b style={{ marginLeft: 6 }}>{visibleTopLevel.length}</b>
            </span>
            <span className="ns-pill admin-beta-pill">
              Loaded: <b style={{ marginLeft: 6 }}>{topLevel.length}</b>
            </span>
          </div>
        </div>
      </div>

      <div style={{ height: 12 }} />

      {loading ? <div className="banner">Loading beta feedback…</div> : null}

      {!loading && visibleTopLevel.length === 0 ? (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900 }}>Nothing to show.</div>
          <div style={{ marginTop: 6, color: "#64748b", fontWeight: 700 }}>
            Try switching filters, or clear search.
          </div>
        </div>
      ) : null}

      {!loading ? (
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          {visibleTopLevel.map((item) => {
            const id = safeStr(item?.id).trim();
            const isOpen = expanded[id] === true;
            const rlist = Array.isArray(repliesByParent[id]) ? repliesByParent[id] : [];
            const hasReplies = rlist.length > 0;

            const author = safeStr(item?.author) || "Anonymous";
            const created = fmtTime(item?.createdAt);
            const text = safeStr(item?.text);

            return (
              <div
                key={id || created || author}
                className="card"
                style={{
                  margin: 0,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 240 }}>
                    <div style={{ fontWeight: 900, color: "#0f172a" }}>{author}</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                      {created ? created : ""}
                      {safeStr(item?.source) ? <span> · {safeStr(item?.source)}</span> : null}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {hasReplies ? (
                      <span className="ns-pill admin-beta-pill" title="This feedback has at least one admin reply.">
                        Replied
                      </span>
                    ) : (
                      <span className="ns-pill admin-beta-pill" title="No reply yet.">
                        Unreplied
                      </span>
                    )}

                    <button className="ns-button ns-button-outline" type="button" onClick={() => toggleExpand(id)}>
                      {isOpen ? "Collapse" : "Expand"}
                    </button>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={() => archiveThread(id)}
                      disabled={busyArchiveId === id}
                      title="Archive this thread"
                      style={{
                        borderColor: "#dc2626",
                        color: "#dc2626",
                      }}
                    >
                      {busyArchiveId === id ? "Archiving..." : "Archive"}
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 10, whiteSpace: "pre-wrap", color: "#0f172a", fontWeight: 700, lineHeight: 1.45 }}>
                  {isOpen ? text : preview(text, 260)}
                </div>

                {isOpen ? (
                  <>
                    {hasReplies ? (
                      <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                        {rlist.map((r) => (
                          <div
                            key={safeStr(r?.id) || Math.random()}
                            style={{
                              padding: 12,
                              borderRadius: 12,
                              background: "#f8fafc",
                              borderLeft: "4px solid #166534",
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                              <div style={{ fontWeight: 900, color: "#0f172a" }}>{safeStr(r?.author) || "Admin"}</div>
                              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                                {fmtTime(r?.createdAt)}
                              </div>
                            </div>
                            <div style={{ marginTop: 8, whiteSpace: "pre-wrap", fontWeight: 700, color: "#0f172a" }}>
                              {safeStr(r?.text)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
                        Reply as admin
                      </div>

                      <textarea
                        value={safeStr(replyMap[id])}
                        onChange={(e) => setReplyMap((prev) => ({ ...prev, [id]: e.target.value }))}
                        placeholder="Write a calm, helpful reply…"
                        rows={4}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: 10,
                          border: "1px solid #e5e7eb",
                          outline: "none",
                          fontWeight: 700,
                          lineHeight: 1.45,
                          resize: "vertical",
                        }}
                      />

                      <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          disabled={busyReplyId === id || !safeStr(replyMap[id]).trim()}
                          onClick={() => submitReply(id)}
                        >
                          {busyReplyId === id ? "Posting..." : "Reply"}
                        </button>

                        <div style={{ color: "#64748b", fontWeight: 700, fontSize: 12 }}>
                          Replies are stored in the beta channel and shown under this feedback.
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {!loading ? (
        <div style={{ marginTop: 14, display: "flex", justifyContent: "center" }}>
          <button className="ns-button ns-button-outline" type="button" onClick={loadMore} disabled={loadingMore || reachedEndRef.current}>
            {reachedEndRef.current ? "No more" : loadingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      ) : null}
    </AdminLayout>
  );
}
